import { SvgCheck, Copy, Stack } from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import { Transition } from 'react-transition-group';
import {
  Breakpoint,
  useDeviceSize,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import React, { useRef, cloneElement, ReactNode } from 'react';
import { getWindow } from 'src/utilities/window';
import { useForceUpdate } from 'src/utilities/useForceUpdate';

export interface ISignupProgressTrackerStepProps {
  isStepActive: boolean;
  isStepComplete: boolean;
  isNextStepCompleteOrActive: boolean;
  stepNumber: number;
  stepName: string;
  stepContent: JSX.Element;
  isInWelcomeModal?: boolean;
  isLastStep: boolean;
}

export interface ISignupStepProps {
  updateMobileStepSize?: () => void;
  children?: ReactNode;
}
const enterDuration = 1000;
const exitDuration = 1000;

const defaultStepContentStyles = {
  transition: `all ${enterDuration}ms ease-in`,
  overflow: 'hidden',
  opacity: 0,
  maxHeight: 0,
};

function getTransitionStyles(ref: HTMLElement) {
  let maxHeight = 0;

  if (ref) {
    maxHeight = ref.scrollHeight;
  }

  return {
    entering: {
      opacity: 1,
      maxHeight,
    },
    entered: {
      opacity: 1,
      maxHeight,
    },
    exiting: {
      opacity: 0,
      maxHeight: 0,
    },
    exited: {
      opacity: 0,
      maxHeight: 0,
    },
  };
}

export function SignupProgressTrackerStep({
  isStepComplete,
  isStepActive,
  isNextStepCompleteOrActive,
  stepNumber,
  stepName,
  stepContent,
  isInWelcomeModal,
  isLastStep,
}: ISignupProgressTrackerStepProps): JSX.Element {
  const { breakpoint } = useDeviceSize(getWindow());
  const forceUpdate = useForceUpdate();

  const isPhone = breakpoint < Breakpoint.sm;
  const stepContentRef = useRef(null);

  const titleTextColor = isStepActive ? 'primaryText' : 'secondaryText';
  const titleTextSize = isPhone ? 'largeBody' : 'body';

  function setContentRef(contentRef: HTMLElement) {
    if (contentRef) {
      stepContentRef.current = contentRef;
    }
  }

  return (
    <li
      className={classnames('signupProgressTrackerStep', {
        mobile: isPhone || isInWelcomeModal,
        complete: isStepComplete,
        borderComplete: isNextStepCompleteOrActive,
        active: isStepActive,
      })}
    >
      <Stack y={isPhone && !isInWelcomeModal ? 'md' : 'none'}>
        <span className="circle">
          {(isStepActive || !isStepComplete) && (
            <Copy
              variant="largeCaption"
              color="invertedPrimaryText"
              fontFamily="secondary"
            >
              {stepNumber}
            </Copy>
          )}
          {isStepComplete && !isStepActive && <SvgCheck size="24px" />}
        </span>
        <span className="nowrap">
          <Copy
            variant={titleTextSize}
            fontFamily="secondary"
            color={titleTextColor}
          >
            {stepName}
          </Copy>
        </span>
      </Stack>

      {stepContent && isPhone && (
        <Transition
          in={isStepActive}
          timeout={{
            enter: enterDuration,
            exit: exitDuration,
          }}
          unmountOnExit
        >
          {(transitionState) => (
            <Stack
              y="lg"
              ref={setContentRef}
              style={{
                ...defaultStepContentStyles,
                ...getTransitionStyles(stepContentRef.current)[transitionState],
                ...(isLastStep && { padding: '0 1px' }),
              }}
            >
              {cloneElement(stepContent, {
                updateMobileStepSize: forceUpdate,
              })}
            </Stack>
          )}
        </Transition>
      )}
    </li>
  );
}
